/**
 * Copyright (c) 2024, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ViewChild } from "@angular/core";
import { HHSeekAndFindOperation } from "app/models/hhseekandfind-operation-dto";
import { HHSeekAndFindService } from "app/services/hhseekandfind.service";
import { SimpleIndefiniteProgressDialogComponent } from "../../simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { HHSeekAndFindDialogComponent } from "../dialog/hhseekandfind-dialog.component";
import { ObjectDefinition } from "app/models/object-definition";
import { ObjectService } from "app/services/object.service";
import { TagDefinition } from "app/models/tag-definition";
import { TagService } from "app/services/tag.service";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";

export const GtinRfidEncodingScheme_Uuid = '55d98e4c-e714-40f0-b2f8-363f4194d359';
export const GraiRfidEncodingScheme_Uuid = 'd30360ad-349e-479d-afc0-03e53812350f';
export const Asset96RfidEncodingScheme_Uuid = '23fc7be4-16b6-4bbe-9412-f9b13c3086e8';

export const Sgtin96Tag_Uuid = '3ded132b-0fae-43d9-9acf-0e7b8a8fc72d';
export const Grai96Tag_Uuid = '616a2f26-05cb-4270-8dde-e05129753b50';
export const Asset96Tag_Uuid = 'f99202b0-016e-4ca4-a96e-dc5671b17a8c';


@Component({
    templateUrl: './hhseekandfind-page.component.html',
    styleUrls: ['./hhseekandfind-page.component.css']
  })
export class HHSeekAndFindPageComponent implements OnInit {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

  /** HHSeekAndFind Operation Edit Dialog displayed when creating / editing Operations */
  @ViewChild(HHSeekAndFindDialogComponent) editDialog: HHSeekAndFindDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  /** Seek and Find Operations being displayed in the UI */
  hhSeekAndFindOperations: HHSeekAndFindOperation[] = [];
  /** Boolean flag indicating if the Seek and Find Operations are being refreshed or not */
  isRefreshingOperations: boolean = false;

  operationBeingEdited: HHSeekAndFindOperation = null;

  allEncodingSchemeTypes: ObjectDefinition[] = [];
  isRefreshingEncodingSchemeTypes: boolean = false;

  allTagTypes: TagDefinition[] = [];
  isRefreshingTagTypes: boolean = false;


  constructor(
    private hhSeekAndFindService: HHSeekAndFindService,
    private objectService: ObjectService,
    private tagService: TagService
  ) { }

  ngOnInit(): void {
    this.refreshOperations();
    this.refreshEncodingSchemeTypes();
    this.refreshTagTypes();
  }



  refreshOperations(): void {
    if (!this.isRefreshingOperations) {
      this.indefiniteProgressDialog?.show("Loading", "Loading Operations...");
      this.isRefreshingOperations = true;
      this._refreshOperations();
    }
  }

  private async _refreshOperations(): Promise<void> {
    // Call the API to get the Operations...
    const ops: HHSeekAndFindOperation[] = [];
    try {
      let newOps = await this.hhSeekAndFindService.getAllSeekAndFindOperations();
      if (newOps?.length > 0) {
        ops.push(...newOps);
      }
    } catch (error) {
      this.indefiniteProgressDialog?.hide();
      this.isRefreshingOperations = false;
      this.handleError(error);
      return;
    }

    // Update the reference being displayed in the UI
    this.hhSeekAndFindOperations = ops;

    this.indefiniteProgressDialog?.hide();
    this.isRefreshingOperations = false;
    return;
  }



  refreshEncodingSchemeTypes(): void {
    if (!this.isRefreshingEncodingSchemeTypes) {
      this.isRefreshingEncodingSchemeTypes = true;
      this._refreshEncodingSchemeTypes();
    }
  }

  private async _refreshEncodingSchemeTypes(): Promise<void> {
    // Call the API to get the Encoding Scheme Types...
    let types: ObjectDefinition[] = [];
    try {
      const gtin = await this.objectService.getObjectForUuid(GtinRfidEncodingScheme_Uuid);
      const asset96 = await this.objectService.getObjectForUuid(Asset96RfidEncodingScheme_Uuid)
      const grai = await this.objectService.getObjectForUuid(GraiRfidEncodingScheme_Uuid);
      if (gtin != null) {
        types.push(gtin);
      }
      if (asset96 != null) {
        types.push(asset96);
      }
      if (grai != null) {
        types.push(grai);
      }
    } catch (error) {
      this.isRefreshingEncodingSchemeTypes = false;
      this.handleError(error);
      return;
    }

    this.allEncodingSchemeTypes = types;

    this.isRefreshingEncodingSchemeTypes = false;
    return;
  }



  refreshTagTypes(): void {
    if (!this.isRefreshingTagTypes) {
      this.isRefreshingTagTypes = true;
      this._refreshTagTypes();
    }
  }

  private async _refreshTagTypes(): Promise<void> {
    // Call the API to get the Tag Types...
    let types: TagDefinition[] = [];
    try {
      const sgtin96 = await this.tagService.getTagDefinitionByUuid(Sgtin96Tag_Uuid);
      const asset96 = await this.tagService.getTagDefinitionByUuid(Asset96Tag_Uuid)
      const grai96 = await this.tagService.getTagDefinitionByUuid(Grai96Tag_Uuid);
      if (sgtin96 != null) {
        types.push(sgtin96);
      }
      if (asset96 != null) {
        types.push(asset96);
      }
      if (grai96 != null) {
        types.push(grai96);
      }
    } catch (error) {
      this.isRefreshingTagTypes = false;
      this.handleError(error);
      return;
    }

    this.allTagTypes = types;

    this.isRefreshingTagTypes = false;
    return;
  }




  onOperationRowClicked(op: HHSeekAndFindOperation): void {
    this.operationBeingEdited = op;
    if (this.operationBeingEdited != null) {
      this.editDialog.showDialog(JSON.parse(JSON.stringify(op)) as HHSeekAndFindOperation, this.allEncodingSchemeTypes, this.allTagTypes);
    }
  }

  onCreateNewClicked(): void {
    console.log(`onCreateNewClicked()`);

    this.operationBeingEdited = new HHSeekAndFindOperation();
    this.operationBeingEdited.enabled = true;
    this.operationBeingEdited.encoding_type = null;
    this.operationBeingEdited.label = "";
    this.operationBeingEdited.main_property = null;
    this.operationBeingEdited.tag_property = null;

    this.editDialog.showDialog(JSON.parse(JSON.stringify(this.operationBeingEdited)) as HHSeekAndFindOperation, this.allEncodingSchemeTypes, this.allTagTypes);
  }

  onDialogCancelClicked(): void {
    console.log(`onDialogCancelClicked()`);
    this.editDialog.hideDialog();
  }

  onDialogConfirmClicked(): void {
    console.log(`onDialogConfirmClicked()`);
    this.editDialog.hideDialog();
  }



  /**
   * Called to handle errors when calling APIs.  
   * Currently this prints them out to the console and displays an alert to the User.
   */
  handleError(error: any): void {
    console.error(`Error detected: ${error}`);
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

}
